import type {
  WixWidgetConfig,
  WidgetConfigResponse,
  OAuthAuthorizeResponse,
  InstallResponse,
  UpdateConfigResponse,
} from "./types";

// Use VITE_API_BASE_URL env var if set, otherwise fall back to defaults
const WIX_API_BASE = import.meta.env.VITE_API_BASE_URL
  ?? (import.meta.env.MODE === "development" ? "http://localhost:3000" : "https://www.bubblav.com");

const WIX_API_ENDPOINT = `${WIX_API_BASE}/api/integrations/wix`;

/**
 * Log into BubblaV and get an access token for subsequent API calls.
 * Used instead of session cookies since the panel runs in a Wix iframe.
 */
export async function loginToBubblaV(
  email: string,
  password: string
): Promise<{ accessToken: string } | null> {
  try {
    const response = await fetch(`${WIX_API_ENDPOINT}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Login failed');
    }
    return await response.json();
  } catch (error) {
    console.error('[BubblaV] Login error:', error);
    return null;
  }
}

/**
 * Connect a Wix site to a BubblaV account.
 * Uses Wix client_credentials OAuth (App ID + Secret + instanceId) server-side.
 * No redirect URI needed.
 */
export async function connectWixSite(
  instanceId: string,
  accessToken: string,
  siteName?: string,
  siteUrl?: string
): Promise<{ websiteId: string } | null> {
  try {
    const response = await fetch(`${WIX_API_ENDPOINT}/connect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ instanceId, siteName, siteUrl }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Connect failed');
    }
    return await response.json();
  } catch (error) {
    console.error('[BubblaV] Connect error:', error);
    return null;
  }
}

/**
 * Initiate OAuth flow by getting a state token and login URL
 */
export async function authorizeOAuth(
  instanceId?: string,
  siteId?: string,
  siteName?: string
): Promise<OAuthAuthorizeResponse | null> {
  try {
    const response = await fetch(`${WIX_API_ENDPOINT}/authorize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        instanceId,
        siteId,
        siteName,
      }),
    });
    if (!response.ok) {
      console.error("[BubblaV] Wix OAuth authorize failed:", response.status);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error("[BubblaV] Wix OAuth authorize error:", error);
    return null;
  }
}

/**
 * Install the Wix app and create connection
 */
export async function installWixApp(
  instanceId: string,
  siteId: string,
  siteName: string,
  siteUrl: string,
  state: string,
  locale = "en",
  isPremium = false
): Promise<InstallResponse | null> {
  try {
    const response = await fetch(`${WIX_API_ENDPOINT}/install`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        instanceId,
        siteId,
        siteName,
        siteUrl,
        state,
        locale,
        isPremium,
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
      console.error("[BubblaV] Wix install failed:", response.status, errorData);
      return { success: false, error: errorData.error || "Installation failed" };
    }
    return await response.json();
  } catch (error) {
    console.error("[BubblaV] Wix install error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}

/**
 * Update widget configuration on server.
 * Auth is done via the Wix `instance` URL parameter (signed JWT from Wix).
 */
export async function updateWidgetConfig(
  websiteId: string,
  config: Partial<WixWidgetConfig>
): Promise<UpdateConfigResponse | null> {
  // Pass the Wix instance token so the backend can verify ownership
  // without requiring a BubblaV user session.
  const instanceToken = new URLSearchParams(window.location.search).get("instance") ?? "";
  try {
    const response = await fetch(`${WIX_API_ENDPOINT}/update-config`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        websiteId,
        config,
        instanceToken,
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
      console.error("[BubblaV] Update config failed:", response.status, errorData);
      return { success: false, error: errorData.error || "Failed to update config" };
    }
    return await response.json();
  } catch (error) {
    console.error("[BubblaV] Update config error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}

/**
 * Validate widget configuration by fetching from API
 */
export async function fetchWidgetConfig(
  websiteId: string
): Promise<WidgetConfigResponse> {
  try {
    const response = await fetch(
      `${WIX_API_BASE}/api/public/widget-config/${encodeURIComponent(websiteId)}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          error: "Invalid Website ID. Please check your BubblaV dashboard.",
        };
      }
      return { success: false, error: "Connection failed. Please try again." };
    }
    const data = await response.json();

    if (data.config) {
      return {
        success: true,
        config: data.config as WixWidgetConfig,
      };
    }

    return { success: false, error: "Invalid website configuration." };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Failed to fetch configuration",
    };
  }
}

/**
 * Get Wix instance data from SDK (if available)
 */
export function getWixInstanceData(): {
  instanceId: string;
  siteId: string;
  siteName: string;
  siteUrl: string;
  locale: string;
} | null {
  // @ts-ignore - Wix SDK global
  if (typeof window !== "undefined" && window.Wix) {
    // @ts-ignore
    const wix = window.Wix as any;
    return {
      instanceId: wix.Utils.getInstanceId() || "",
      siteId: wix.Utils.getSiteId() || "",
      siteName: wix.Utils.getSiteName() || "",
      siteUrl: wix.Utils.getSectionUrl()?.baseUrl || "",
      locale: wix.Utils.getLocale() || "en",
    };
  }
  return null;
}

/**
 * Get OAuth token from Wix SDK (if available)
 */
export async function getWixOAuthToken(): Promise<string | null> {
  // @ts-ignore - Wix SDK global
  if (typeof window !== "undefined" && window.Wix) {
    // @ts-ignore
    const wix = window.Wix as any;
    try {
      const token = await wix.Utils.getOAuthToken();
      return token || null;
    } catch (error) {
      console.error("[BubblaV] Failed to get Wix OAuth token:", error);
      return null;
    }
  }
  return null;
}

/**
 * Resize widget iframe
 */
export function resizeWidget(width: number, height: number): void {
  // @ts-ignore - Wix SDK global
  if (typeof window !== "undefined" && window.Wix) {
    // @ts-ignore
    window.Wix.resizeWidget?.(width, height);
  }
}

/**
 * Close widget panel
 */
export function closeWidgetPanel(): void {
  // @ts-ignore - Wix SDK global
  if (typeof window !== "undefined" && window.Wix) {
    // @ts-ignore
    window.Wix.closeWindow?.();
  }
}
